import LinkifyIt from 'linkify-it';
import { visit } from 'unist-util-visit';
import type { Definition, Link, PhrasingContent, Root, Text } from 'mdast';
import { EMAIL_REGEX } from './emailValidation';

const linkify = new LinkifyIt();

// linkify-it ships a curated TLD list that predates several gTLDs, `.fun`
// among them — which is Meetball's own domain, so without this a share link
// pasted as `go.meetball.fun/xK9mP2Qr` renders as dead text. Extending the
// list (rather than loosening the check to any `.xx` suffix) keeps
// `agenda.pdf` and `photo.jpg` from being promoted to links to hosts that do
// not exist.
linkify.tlds(['fun', 'app', 'dev'], true);

/**
 * A leading `scheme:` — https:, mailto:, and also javascript: (the sanitizer's
 * job, not ours).
 *
 * No `.` in the class: no real scheme contains one, and allowing it made a
 * `host:port` destination like `tickets.example.com:8443/buy` read as the
 * scheme `tickets.example.com`.
 */
const HAS_SCHEME = /^[a-z][a-z0-9+-]*:/i;

/**
 * Scheme-relative (`//host/path`) — a browser resolves it against the page's own
 * protocol and lands on `host`, so despite the leading `/` it is not in-app.
 */
const SCHEME_RELATIVE_PREFIX = '//';

/** In-app path, fragment, query or relative path — the router owns these. */
const IS_IN_APP = /^[/#?.]/;

/** A path, query or fragment separator (`\` included: browsers read it as `/`). */
const HAS_PATH = /[/?#\\]/;

/**
 * Resolves a link destination to something safe to put in an `href`.
 *
 * Applied by `remarkNormalizeLinkProtocols` to the destination the parser has
 * already resolved. That is what a pre-parse string rewrite cannot get right:
 * it has to re-find the destination with a regex, and a regex cannot tell
 * `[a](example.com)` in prose from the same characters inside a code sample,
 * nor separate a destination from its `"title"`.
 *
 * - a scheme the author typed is preserved, lower-cased (`javascript:` included
 *   — this runs on the mdast tree, so rehype-sanitize is still downstream and
 *   still the thing that drops it; rewriting one here would only launder it past
 *   the only check that rejects it);
 * - a scheme-relative `//host` gets `https:`, because it leaves the origin;
 * - in-app paths (`/spaces`), anchors (`#agenda`) and relative paths pass through;
 * - a bare address becomes `mailto:`, which can only ever open a mail client;
 * - a real hostname — validated against linkify-it's TLD table, so `agenda.pdf`
 *   and `notaurl` do not qualify — gets `https://`;
 * - anything else resolves to `''`, which renders the label as plain text
 *   rather than a styled anchor that hard-navigates the SPA to a dead route.
 */
export function normalizeLinkUrl(url: string): string {
  if (!url) return url;

  // Lower-case the scheme and nothing else. rehype-sanitize compares its
  // protocol whitelist case-sensitively, so `HTTP://example.com` — what iOS
  // autocapitalisation produces from a pasted line — lost its href entirely and
  // rendered as plain text. The rest of the URL keeps the author's casing,
  // because a path and a query really can be case-significant.
  const scheme = HAS_SCHEME.exec(url);
  if (scheme) return scheme[0].toLowerCase() + url.slice(scheme[0].length);

  if (url.startsWith(SCHEME_RELATIVE_PREFIX)) return `https:${url}`;
  if (IS_IN_APP.test(url)) return url;
  if (!HAS_PATH.test(url) && EMAIL_REGEX.test(url)) return `mailto:${url}`;

  // Only promote what linkify-it recognises as a whole, schemeless host. Its
  // TLD table is what keeps `[spec](agenda.pdf)` from becoming a live link to a
  // host that does not exist, and requiring the match to span the entire
  // destination is what keeps `trusted.com@evil.com/path` — where everything
  // before the `@` is userinfo and the browser lands on evil.com — out.
  const matches = linkify.match(url);
  const match = matches?.[0];
  const isWholeHost =
    matches?.length === 1 &&
    match !== undefined &&
    match.index === 0 &&
    match.lastIndex === url.length &&
    match.schema === '';
  return isWholeHost ? `https://${url}` : '';
}

/**
 * remark plugin: turns bare URLs in prose into links.
 *
 * GFM autolinks only cover `http(s)://`, `www.` and emails, so a bare
 * `example.com` still needs promoting. Doing it on `text` nodes rather than on
 * the source string is what keeps it honest — a `text` node never covers a code
 * span, a fenced block, or the label of an existing link, so none of those can
 * be rewritten. The string version double-wrapped any link whose label was
 * itself URL-shaped (`[example.com](https://example.com)`), which rendered with
 * literal brackets and sent the tap to the wrong host.
 *
 * A scheme the author typed is preserved as-is; only schemeless hosts are
 * upgraded, and emails keep their `mailto:`.
 */
export function remarkLinkifyBareUrls() {
  return (tree: Root): undefined => {
    visit(tree, 'text', (node: Text, index, parent) => {
      // A link's own label must never gain a nested link — CommonMark forbids
      // them, and the result renders as literal brackets. `linkReference` is the
      // same thing in reference form (`[example.com][1]`), where the outer
      // destination lives on a definition node: missing it produced a real
      // <a> inside an <a>, and the INNER one takes the click, so the author's
      // destination is the one that loses.
      if (
        !parent ||
        index === undefined ||
        parent.type === 'link' ||
        parent.type === 'linkReference'
      )
        return;

      const matches = linkify.match(node.value);
      if (!matches) return;

      const replacement: PhrasingContent[] = [];
      let cursor = 0;
      for (const match of matches) {
        if (match.index > cursor) {
          replacement.push({ type: 'text', value: node.value.slice(cursor, match.index) });
        }
        replacement.push({
          // `schema` is '' only when the author typed no scheme at all. Keying
          // off it rather than the resolved `url` matters twice over: linkify-it
          // resolves a schemeless host to http://, and an email to mailto:.
          url: match.schema === '' ? `https://${match.raw}` : match.url,
          type: 'link',
          children: [{ type: 'text', value: match.text }],
        });
        cursor = match.lastIndex;
      }
      if (cursor < node.value.length) {
        replacement.push({ type: 'text', value: node.value.slice(cursor) });
      }

      parent.children.splice(index, 1, ...replacement);
      // Resume past the nodes just inserted, so their text is not re-scanned.
      return index + replacement.length;
    });
  };
}

/**
 * remark plugin: gives every link destination a protocol the author would
 * recognise, via {@link normalizeLinkUrl}.
 *
 * Must run before `rehype-sanitize`: the sanitizer reads everything before the
 * first `:` as a protocol, so a `host:port` destination like
 * `tickets.example.com:8443/buy` looks like an unknown scheme and loses its
 * href entirely. Normalising on the mdast tree happens well ahead of that, and
 * — unlike the string pass it replaces — cannot reach inside a code span.
 *
 * `definition` is visited alongside `link` because a reference link
 * (`[label][1]`, `[label][]`, `[label]`) keeps its destination on the definition
 * node until remark-rehype resolves it. Visiting only `link` exempted every
 * reference form from all of the above.
 */
export function remarkNormalizeLinkProtocols() {
  return (tree: Root): undefined => {
    visit(tree, ['link', 'definition'], node => {
      const destination = node as Link | Definition;
      destination.url = normalizeLinkUrl(destination.url);
    });
  };
}
